import {
    Box,
    Collapse,
    Divider,
    Fade,
    Grid,
    Grow,
    IconButton,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import { GroupSelector } from "components/Classifiers/Common/GroupSelector";
import { TitledSection } from "components/Classifiers/Common/TitledSection";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useStore } from "utils/useStore";
import {
    LLMClassifierBuilderStore,
    LLMClassifierDefinedOutputsType,
} from "../../Stores/LLMClassifierBuilderStore";
import { DataLabelingModuleAndQuestionSelectors } from "../../Components/DataLabelingModuleAndQuestionSelector";
import { RubricSection } from "../../Components/Rubric/RubricSection";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";
import AcxSwitch from "components/UI/AcxSwitch";
import { LLMClassifierPurpose } from "models/LLMs/LLMClassifierPurpose";
import {
    Add,
    CancelRounded,
    ChevronRight,
    ClassRounded,
    InfoOutlined,
    InfoRounded,
    LinkRounded,
} from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import { LLMPurposeIcon } from "../../Components/LLMPurposeIcon";
import { ModelVisibilitySelector } from "../../Components/ModelVisibilitySelector";

const fadeTimeout = 200;

export const LLMClassifierDetails = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const purpose = store.classifier.implementation.llmPurpose;

    return (
        <Grid container item direction="column" flexWrap="nowrap" rowGap={2}>
            <LLMPurposeSelector
                purpose={purpose}
                onPurposeSelect={(purpose) => {
                    store.classifier.implementation.setLLMPurpose(purpose);
                }}
            />
            {purpose !== undefined && (
                <Collapse
                    in
                    timeout={fadeTimeout}
                    style={{ transitionDelay: `${fadeTimeout}ms` }}
                >
                    {purpose === LLMClassifierPurpose.Quality && (
                        <QualityPurposeDetails />
                    )}
                    {purpose === LLMClassifierPurpose.Insights && (
                        <InsightsPurposeDetails />
                    )}
                    {purpose === LLMClassifierPurpose.ExtractData && (
                        <ExtractDataPurposeDetails />
                    )}
                </Collapse>
            )}
        </Grid>
    );
});

function DetailsContainer(props: React.PropsWithChildren) {
    return (
        <Grid container item direction="column" flexWrap="nowrap" rowGap={2}>
            {props.children}
        </Grid>
    );
}

const ModelDetailsSection = observer((props: React.PropsWithChildren) => {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const theme = useTheme();

    // Extract Data models roll up into a flat (single-level) group, so cap the selector at depth 1.
    const groupMaxDepth =
        store.classifier.implementation.llmPurpose ===
        LLMClassifierPurpose.ExtractData
            ? 1
            : 2;

    return (
        <TitledSection
            title="Model Details"
            // variant="bordered"
        >
            <Grid container direction="column" rowGap={2}>
                <Grid
                    container
                    item
                    flexWrap="nowrap"
                    columns={2}
                    columnGap={2}
                >
                    <Grid item xs={2}>
                        <AcxMainTextField
                            id="model-name"
                            data-testid="model-name"
                            labelText="Model Name"
                            fullWidth
                            required
                            labelSx={{ color: "inherit" }}
                            value={store.classifier.name}
                            error={stepper.hasError.nameMissing}
                            hideErrorIcon
                            onChange={(event) => {
                                store.classifier.setName(event.target.value);
                                stepper.resetError("nameMissing");
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid container item flexWrap="nowrap">
                    <Grid item xs={12}>
                        <GroupSelector
                            id="model-group"
                            inputLabel="Model Group"
                            maxDepth={groupMaxDepth}
                            labelIcon={
                                <Tooltip title="Used to group model results into a shared Signals dashboard. Each Model Group has its own dashboard.">
                                    <InfoRounded
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            color: theme.palette.gray[400],
                                        }}
                                    />
                                </Tooltip>
                            }
                            defaultValue={store.classifier.classifierGroup}
                            onChange={(selection) =>
                                store.classifier.setClassifierGroup(selection)
                            }
                        />
                    </Grid>
                </Grid>
                <Grid item>
                    <AcxMainTextField
                        id="model-description"
                        labelText="Description"
                        fullWidth
                        multiline
                        multilinePadding
                        rowsMin={3}
                        value={store.classifier.description}
                        onChange={(event) =>
                            store.classifier.setDescription(event.target.value)
                        }
                    />
                </Grid>
                <Grid item>
                    <ModelVisibilitySelector />
                </Grid>
                {props.children}
            </Grid>
        </TitledSection>
    );
});

const QualityPurposeDetails = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);

    return (
        <DetailsContainer>
            <ModelDetailsSection>
                <Grid
                    container
                    item
                    flexWrap="nowrap"
                    alignItems="center"
                    columnGap={1}
                >
                    <Grid item>
                        <AcxSwitch
                            id="test-without-labels"
                            checked={store.isTestWithoutLabels}
                            onChange={() => {
                                store.toggleTestWithoutLabels();
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <label htmlFor="test-without-labels">
                            Test without labels: Use this option to test models
                            without needing pre-labeled data
                        </label>
                    </Grid>
                </Grid>
            </ModelDetailsSection>
            <LinkedModuleSection />
            <RubricSection />
        </DetailsContainer>
    );
});

interface SelectableOptionProps {
    id?: string;
    title: string;
    description: string;
    icon: React.ComponentType<{ htmlColor?: string }>;
    isSelected?: boolean;
    onClick: () => void;
}

const SelectableOption = ({
    id,
    title,
    description,
    icon: Icon,
    isSelected = false,
    onClick,
}: SelectableOptionProps) => (
    <Box
        id={id}
        sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            border: "1px solid transparent",
            "&:hover": {
                borderColor: "primary.300",
                bgcolor: "primary.50",
            },
            transition: "all 0.2s",
            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.07)",
            bgcolor: "white.main",
            borderRadius: 2,
            p: 2,
            cursor: "pointer",
            ...(isSelected && {
                borderColor: "primary.300",
                bgcolor: "primary.50",
            }),
        }}
        onClick={onClick}
        role="button"
    >
        <Box display="flex" alignItems="center">
            <ListItemIcon
                sx={{
                    borderRadius: 1,
                    p: 1,
                    width: "fit-content",
                    minWidth: 0,
                    background:
                        "linear-gradient(132deg, #3664D6 3.97%, #1C3470 170.3%)",
                    mr: 2,
                }}
            >
                <Icon htmlColor="white" />
            </ListItemIcon>
            <Box>
                <Typography
                    fontWeight="bold"
                    color="gray.600"
                    fontSize="14px"
                    lineHeight="150%"
                >
                    {title}
                </Typography>
                <Typography color="gray.600">{description}</Typography>
            </Box>
        </Box>
    </Box>
);

const InsightsPurposeDetails = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);

    const isOutputsUserDefined =
        store.definedOutputsType ===
        LLMClassifierDefinedOutputsType.UserDefined;
    const isOutputsFromQuestion =
        store.definedOutputsType ===
        LLMClassifierDefinedOutputsType.FromQuestion;

    return (
        <DetailsContainer>
            <ModelDetailsSection />
            {!store.isEditing && (
                <Grid
                    container
                    item
                    direction="row"
                    flexWrap="nowrap"
                    columns={12}
                    columnGap={1}
                    alignItems="stretch"
                    width="100%"
                >
                    <Grid item xs={true} sx={{ display: "flex" }}>
                        <SelectableOption
                            id="link-module"
                            title="Link a module to define results"
                            description="Choose a module question to automatically pull in the result options (i.e. Meets, Does Not Meet, N/A). Linking a module also enables confusion matrix testing."
                            icon={LinkRounded}
                            isSelected={isOutputsFromQuestion}
                            onClick={() => {
                                if (isOutputsFromQuestion) return;

                                store.setDefinedOutputsType(
                                    LLMClassifierDefinedOutputsType.FromQuestion,
                                );

                                store.setLinkedModule();
                                store.setLinkedQuestion();
                                store.classifier.setOutputs([]);
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ display: "flex", alignItems: "center" }}>
                        <Typography
                            sx={{ color: "primary.600", fontWeight: "bold" }}
                        >
                            OR
                        </Typography>
                    </Grid>
                    <Grid item xs={true} sx={{ display: "flex" }}>
                        <SelectableOption
                            id="define-custom-results"
                            title="Define custom valid results"
                            description="Use this when your model isn't tied to a module. Enter the valid results you expect the model to output (i.e. Shipment Delay, Caregiver Involved, Negative Sentiment)."
                            icon={ClassRounded}
                            isSelected={isOutputsUserDefined}
                            onClick={() => {
                                if (isOutputsUserDefined) return;

                                store.setDefinedOutputsType(
                                    LLMClassifierDefinedOutputsType.UserDefined,
                                );

                                store.classifier.setOutputs([]);
                                store.classifier.addOutput();
                            }}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item>
                <LinkedModuleSection />
            </Grid>
            <Grid item>
                <RubricSection />
            </Grid>
            {(store.definedOutputsType ===
                LLMClassifierDefinedOutputsType.UserDefined ||
                (store.definedOutputsType ===
                    LLMClassifierDefinedOutputsType.FromQuestion &&
                    store.classifier.outputs.length > 0) ||
                store.isEditing) && (
                <Grid item>
                    <ValidResultsSection />
                </Grid>
            )}
        </DetailsContainer>
    );
});

const ValidResultsSection = observer(() => {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);

    return (
        <TitledSection
            title="Valid Results"
            subtitle="List the possible results this model is expected to return. These values must match exactly in order to be used for filtering and reporting. If the model returns something else during testing, we'll flag it as an error."
        >
            <Grid container direction="column" flexWrap="nowrap" rowGap={2}>
                {!store.canModifyOutputs && (
                    <Grid
                        container
                        item
                        justifyContent="space-between"
                        columnGap={1}
                        p={1}
                        borderRadius={1}
                        bgcolor="primary.100"
                        borderColor="primary.50"
                    >
                        <Grid container item columnGap={1} alignItems="center">
                            <InfoOutlined color="primary" />
                            <Grid item>
                                <Typography
                                    color="primary"
                                    component="span"
                                    fontWeight="bold"
                                >
                                    Due to data consistency concerns, valid
                                    results can't be changed after publishing.
                                    Please create a new model if you want to
                                    change results.
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid item></Grid>
                    </Grid>
                )}
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={1}
                >
                    <Grid item>
                        <Typography color="gray.500" fontWeight="bold">
                            Result
                        </Typography>
                    </Grid>
                    {store.classifier.outputs.map((output, idx) => (
                        <Grid
                            container
                            item
                            key={output.id}
                            alignItems="center"
                            flexWrap="nowrap"
                            columnGap={1}
                        >
                            <Grid item width="100%">
                                <AcxMainTextField
                                    id={output.id}
                                    fullWidth
                                    value={output.result}
                                    onChange={(e) =>
                                        store.classifier.updateOutputById(
                                            output.id,
                                            e.target.value,
                                        )
                                    }
                                    placeholderText="Enter valid result..."
                                    isDisabled={!store.canModifyOutputs}
                                    error={
                                        stepper.hasError
                                            .validResultsNotDefined &&
                                        !output.result
                                    }
                                    hideErrorIcon
                                />
                            </Grid>
                            {(idx > 0 || store.classifier.outputs.length > 1) &&
                                store.canModifyOutputs && (
                                    <Grid item>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                store.classifier.removeOutputById(
                                                    output.id,
                                                )
                                            }
                                        >
                                            <CancelRounded fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                )}
                        </Grid>
                    ))}
                    {store.canModifyOutputs && (
                        <AcxButton
                            startIcon={<Add />}
                            onClick={() => store.classifier.addOutput()}
                            color="white"
                            sx={{ color: "primary.500", mt: 1 }}
                        >
                            Valid Result
                        </AcxButton>
                    )}
                </Grid>
            </Grid>
        </TitledSection>
    );
});

const ExtractDataPurposeDetails = observer(() => {
    return (
        <DetailsContainer>
            <ModelDetailsSection />
            <LinkedModuleSection />
            <RubricSection />
        </DetailsContainer>
    );
});

// Shown for every purpose; the rubric and the Data step use this question.
function LinkedModuleSection() {
    return (
        <TitledSection title="Linked Module">
            <DataLabelingModuleAndQuestionSelectors />
        </TitledSection>
    );
}

interface LLMPurposeSelectorProps {
    onPurposeSelect: (purpose: LLMClassifierPurpose) => void;
    purpose?: LLMClassifierPurpose;
}

const purposes = [
    {
        id: "quality",
        title: "Score Questions",
        purpose: LLMClassifierPurpose.Quality,
        description:
            "Evaluate agent performance based on static, labeled questions",
    },
    {
        id: "insights",
        title: "Create Insights",
        purpose: LLMClassifierPurpose.Insights,
        description: `Surface moments in calls that indicate specific events or patterns (e.g. "caller mentions missing documentation"), without scoring agents`,
    },
    {
        title: "Extract Data",
        purpose: LLMClassifierPurpose.ExtractData,
        description:
            "Extract signal from calls and write structured values into designated metadata fields (e.g. sentiment, lot #, drug name)",
    },
];

const LLMPurposeSelector = observer(function LLMPurposeSelector({
    onPurposeSelect,
    purpose,
}: LLMPurposeSelectorProps) {
    const [selectedPurpose, setSelectedPurpose] = useState(purpose);
    const [fadeOut, setFadeOut] = useState(false);

    const handleSelect = (purpose: LLMClassifierPurpose) => {
        setFadeOut(true);
        setTimeout(() => {
            setSelectedPurpose(purpose);
            setFadeOut(false);
        }, fadeTimeout);
    };

    const selectedItem = purposes.find((i) => i.purpose === selectedPurpose);

    return (
        <TitledSection
            title="Model Purpose"
            // variant="bordered"
        >
            <Box sx={{ width: "100%" }}>
                {selectedItem && !fadeOut && (
                    <Grow in timeout={fadeTimeout}>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "primary.300",
                                bgcolor: "primary.50",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Box display="flex" alignItems="center">
                                <ListItemIcon
                                    sx={{
                                        borderRadius: 1,
                                        p: 1,
                                        width: "fit-content",
                                        minWidth: 0,
                                        background:
                                            "linear-gradient(132deg, #3664D6 3.97%, #1C3470 170.3%)",
                                        mr: 2,
                                    }}
                                >
                                    <LLMPurposeIcon
                                        purpose={selectedItem.purpose}
                                    />
                                </ListItemIcon>
                                <Box>
                                    <Typography
                                        fontWeight="bold"
                                        color="gray.600"
                                        fontSize="16px"
                                        lineHeight="150%"
                                    >
                                        {selectedItem.title}
                                    </Typography>
                                    <Typography color="gray.600">
                                        {selectedItem.description}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grow>
                )}
                {!selectedItem && (
                    <Fade in={!fadeOut} timeout={fadeTimeout}>
                        <Grid container direction="column" rowGap={1}>
                            {purposes.map((item, index) => (
                                <React.Fragment key={item.id}>
                                    <ListItemButton
                                        id={item.id}
                                        data-testid={item.id}
                                        onClick={() => {
                                            handleSelect(item.purpose);
                                            onPurposeSelect(item.purpose);
                                        }}
                                        sx={{
                                            borderRadius: 1,
                                            border: "1px solid transparent",
                                            transition: "all 0.2s",
                                            "&:hover": {
                                                bgcolor: "primary.50",
                                                borderColor: "primary.300",
                                            },
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                borderRadius: 1,
                                                p: 1,
                                                width: "fit-content",
                                                minWidth: 0,
                                                background:
                                                    "linear-gradient(132deg, #3664D6 3.97%, #1C3470 170.3%)",
                                                mr: 2,
                                            }}
                                        >
                                            <LLMPurposeIcon
                                                purpose={item.purpose}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item.title}
                                            secondary={item.description}
                                            primaryTypographyProps={{
                                                fontWeight: "bold",
                                                color: "gray.600",
                                                fontSize: "16px",
                                                lineHeight: "150%",
                                            }}
                                            secondaryTypographyProps={{
                                                color: "gray.600",
                                            }}
                                        />
                                        <ChevronRight
                                            sx={{ color: "gray.400" }}
                                        />
                                    </ListItemButton>
                                    {index < purposes.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </Grid>
                    </Fade>
                )}
            </Box>
        </TitledSection>
    );
});
